import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _debounce from 'lodash/debounce';
import React, { useEffect, useMemo, useState } from 'react';

import { DJANGO_OPERATOR_LABELS, DjangoOperator } from '@/types/logic';
import { Button, Checkbox, Input, Select } from '@/ui';
import { OptionType } from '@/ui/components/Form/Select/types';
import { cn } from '@/ui/lib/utils';

import { useQueryBuilderConfig } from './QueryBuilderContext';
import type { LogicElementEditorProps as Props } from './types';

function LogicElementEditor({ fieldOptions, element, onChange, onDelete }: Props) {
  const {
    labels,
    elementLayout,
    hideFieldSelect,
    hideOperatorSelect,
    hideNegate,
    allowMultipleValues,
    classNames
  } = useQueryBuilderConfig();

  const fieldChoices: OptionType<string>[] = useMemo(
    () => fieldOptions.map((option) => ({ value: option.field, label: option.label })),
    [fieldOptions]
  );

  const selectedFieldOption = fieldOptions.find((option) => option.field === element.field);

  const operatorChoices: OptionType<string>[] = useMemo(
    () =>
      (selectedFieldOption?.operators ?? []).map((operator) => ({
        value: operator,
        label: DJANGO_OPERATOR_LABELS[operator] ?? operator
      })),
    [selectedFieldOption]
  );

  const handleFieldChange = (option: OptionType<string> | null) => {
    if (!option) return;
    const nextFieldOption = fieldOptions.find((field) => field.field === option.value);
    onChange(element.id, {
      field: option.value,
      comparison_operator: nextFieldOption?.operators[0],
      // A choice field validates `value` against its allowed choices even when
      // empty, so `null` (read as [null]) is rejected -- an empty array isn't.
      value: nextFieldOption?.value_choices?.length ? [] : null,
      is_negated: false
    });
  };

  const handleOperatorChange = (option: OptionType<string> | null) => {
    if (!option) return;
    onChange(element.id, { comparison_operator: option.value });
  };

  const handleValueChoicesChange = (options: OptionType<string>[]) => {
    onChange(element.id, { value: options.map((option) => option.value) });
  };

  // Still stored as a single-item array -- that's what the backend's
  // CONTAINS lookup expects regardless of how many values the UI lets you
  // pick at once.
  const handleSingleValueChoiceChange = (option: OptionType<string> | null) => {
    onChange(element.id, { value: option ? [option.value] : [] });
  };

  const handleNegateChange = (checked: boolean) => {
    onChange(element.id, { is_negated: checked });
  };

  // Debounce the plain-text value input so a PATCH isn't fired on every
  // keystroke -- local state keeps the field responsive while typing.
  const [localTextValue, setLocalTextValue] = useState(
    element.value === null || element.value === undefined ? '' : String(element.value)
  );

  useEffect(() => {
    setLocalTextValue(
      element.value === null || element.value === undefined ? '' : String(element.value)
    );
    // Only re-sync when a different element's value arrives from outside --
    // avoid clobbering in-progress typing on every render.
  }, [element.id, element.value]);

  const debouncedTextChange = useMemo(
    () => _debounce((id: string, value: string) => onChange(id, { value }), 500),
    [onChange]
  );

  useEffect(() => () => debouncedTextChange.cancel(), [debouncedTextChange]);

  const handleTextValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalTextValue(event.target.value);
    debouncedTextChange(element.id, event.target.value);
  };

  const handleTextValueBlur = () => {
    debouncedTextChange.flush();
  };

  // An empty `value_choices` array (as opposed to null/undefined) means the
  // field declares no fixed choices at all -- treat it the same as a plain
  // text field.
  const valueChoices =
    selectedFieldOption?.value_choices && selectedFieldOption.value_choices.length > 0
      ? selectedFieldOption.value_choices
      : null;
  const allowCustomValue = !!selectedFieldOption?.allow_custom_value;

  const selectedValueChoices: OptionType<string>[] = useMemo(() => {
    if (!valueChoices) return [];
    const selectedValues = Array.isArray(element.value) ? element.value : [];
    return selectedValues.map((value) => {
      const choice = valueChoices.find((choice) => choice.value === value);
      // A previously-added custom value won't match any known choice -- fall
      // back to showing the raw value as its own label.
      return choice ? { value: choice.value, label: choice.label } : { value, label: value };
    });
  }, [valueChoices, element.value]);

  const selectedValueChoice: OptionType<string> | null = selectedValueChoices[0] ?? null;

  const showsValueInput = selectedFieldOption?.operators.includes(DjangoOperator.ISNULL)
    ? element.comparison_operator !== DjangoOperator.ISNULL
    : true;

  return (
    <div
      className={cn(
        'logic-element-editor flex items-center gap-2',
        elementLayout === 'wrap' ? 'flex-wrap' : 'flex-nowrap overflow-x-auto',
        classNames.element
      )}>
      {!hideFieldSelect && (
        <Select
          className="min-w-[10rem]"
          label="Field"
          showLabel={false}
          placeholder={labels.fieldPlaceholder}
          options={fieldChoices}
          selectedOption={fieldChoices.find((option) => option.value === element.field) ?? null}
          onChange={handleFieldChange}
          clearable={false}
        />
      )}

      {!hideOperatorSelect && (
        <Select
          className="min-w-[10rem]"
          label="Operator"
          showLabel={false}
          placeholder={labels.operatorPlaceholder}
          options={operatorChoices}
          selectedOption={
            operatorChoices.find((option) => option.value === element.comparison_operator) ?? null
          }
          onChange={handleOperatorChange}
          disabled={!selectedFieldOption}
          clearable={false}
        />
      )}

      {showsValueInput &&
        (valueChoices ? (
          allowMultipleValues ? (
            <Select
              className="min-w-[12rem]"
              label="Value"
              showLabel={false}
              placeholder={
                allowCustomValue ? labels.valueWithCustomPlaceholder : labels.valuePlaceholder
              }
              multiple
              creatable={allowCustomValue}
              options={valueChoices.map((choice) => ({ value: choice.value, label: choice.label }))}
              selectedOptions={selectedValueChoices}
              onChange={handleValueChoicesChange}
            />
          ) : (
            <Select
              className="min-w-[12rem]"
              label="Value"
              showLabel={false}
              placeholder={labels.valuePlaceholder}
              options={valueChoices.map((choice) => ({ value: choice.value, label: choice.label }))}
              selectedOption={selectedValueChoice}
              onChange={handleSingleValueChoiceChange}
            />
          )
        ) : (
          <Input
            id={`logic-element-value-${element.id}`}
            className="min-w-[10rem]"
            inputClassName="py-2"
            placeholder={labels.textValuePlaceholder}
            value={localTextValue}
            onChange={handleTextValueChange}
            onBlur={handleTextValueBlur}
          />
        ))}

      {!hideNegate && selectedFieldOption?.can_negate && (
        <Checkbox
          label={labels.negate}
          checked={element.is_negated}
          onChange={handleNegateChange}
        />
      )}

      <Button
        aria-label={labels.removeCondition}
        variant="ghost"
        color="destructive"
        size="icon-sm"
        onClick={() => onDelete(element.id)}>
        <FontAwesomeIcon icon={faTrash} />
      </Button>
    </div>
  );
}

export default React.memo(LogicElementEditor);
